import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Select, Text } from 'v3/components';
import CheckBox from 'components/CheckBox';
import TextArea from 'v3/components/TextArea';
import UploadChecklistMedia from 'v3/components/UploadChecklistMedia';
import { FaAdjust, FaBars, FaCamera, FaPlayCircle, FaCheckCircle, FaCheckSquare, FaImage, FaPlus, FaTruck, FaTrailer, FaQrcode } from 'react-icons/fa';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import api, { cooperplaceApi } from 'services/api';
import {
  AddAnswerButton,
  AddAnswerButtonContainer,
  QuestionGeneralInfoContainer,
  QuestionTypeFieldContainer,
} from './styles';
import { SortableAnswer } from './SortableAnswerComponent';
import {
  DESCRIPTIVE_QUESTION_SLUG,
  IMAGE_QUESTION_SLUG,
  LOGICAL_QUESTION_ANSWERS,
  LOGICAL_QUESTION_SLUG,
  QUESTION_TYPES_ALLOWED_TO_CREATE_OCCURRENCE,
  QUESTION_TYPES_ALLOWED_TO_HAVE_CORRECT_ANSWER,
  QUESTION_TYPES_ALLOWED_TO_HAVE_CHILD_QUESTION,
  QUESTION_TYPES_ALLOWED_TO_REGISTER_ANSWER,
  QUESTION_TYPES_ALLOWED_TO_REQUEST_ATTACHMENT,
  QUESTION_TYPES_WITH_SINGLE_CORRECT_ANSWER,
  UNIQUE_QUESTION_SLUG,
  VEHICLE_PLATE_QUESTION_SLUG,
  IMPLEMENT_PLATE_QUESTION_SLUG,
  CODE_READER_QUESTION_SLUG,
  VIDEO_VISUALIZATION_QUESTION_SLUG,
} from '../../shared/utils';

const FIXED_QUESTION_TYPES = [
  VEHICLE_PLATE_QUESTION_SLUG,
  IMPLEMENT_PLATE_QUESTION_SLUG,
  CODE_READER_QUESTION_SLUG,
];

const FIXED_QUESTION_TEXT_BY_SLUG = {
  [VEHICLE_PLATE_QUESTION_SLUG]: 'Informe a placa do veículo de tração',
  [IMPLEMENT_PLATE_QUESTION_SLUG]: 'Informe a placa do implemento / carroceria',
  [CODE_READER_QUESTION_SLUG]: 'Realize a leitura do código',
};

const MAX_VIDEO_SIZE_BYTES = 1.5 * 1024 * 1024 * 1024;
const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mpeg', 'mpg', 'mkv', 'mov'];
const ALLOWED_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/x-m4v',
  'video/mpeg',
  'video/quicktime',
  'video/x-matroska',
];
const VIDEO_ACCEPTED_FORMATS_LABEL = ALLOWED_VIDEO_EXTENSIONS
  .map(extension => `.${extension}`)
  .join(', ');
const MAX_VIDEO_SIZE_LABEL = '1,5 GB';

export function RegisterComponent({
  isRegister,
  question,
  setQuestion,
  questionType,
  setQuestionType,
  childQuestion,
  setChildQuestion,
  childQuestionType,
  setChildQuestionType,
  errors,
  setErrors,
}) {
  const [groupOptions, setGroupOptions] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const normalizedSlug = (questionType?.slug || '').toLowerCase();
  const isVideoVisualizationType =
    normalizedSlug === VIDEO_VISUALIZATION_QUESTION_SLUG;
  const isFixedQuestionType = FIXED_QUESTION_TYPES.includes(normalizedSlug);
  const canRegisterAnswer = !isFixedQuestionType && !isVideoVisualizationType;
  const canShowInsertAnswerButton = !isVideoVisualizationType;
  const [videoFiles, setVideoFiles] = useState([]);
  const [videoUploadError, setVideoUploadError] = useState('');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');

  const questionTypeOptions = [
    {
      slug: 'descriptive',
      emoji: <FaBars />,
    },
    {
      slug: 'unique',
      emoji: <FaCheckCircle />,
    },
    {
      slug: 'multiple',
      emoji: <FaCheckSquare />,
    },
    {
      slug: 'image',
      emoji: <FaImage />,
    },
    {
      slug: 'logic',
      emoji: <FaAdjust />,
    },
    {
      slug: 'video',
      emoji: <FaCamera />,
    },
    {
      slug: VIDEO_VISUALIZATION_QUESTION_SLUG,
      emoji: <FaPlayCircle />,
    },
    {
      slug: VEHICLE_PLATE_QUESTION_SLUG,
      emoji: <FaTruck />,
    },
    {
      slug: IMPLEMENT_PLATE_QUESTION_SLUG,
      emoji: <FaTrailer />,
    },
    {
      slug: CODE_READER_QUESTION_SLUG,
      emoji: <FaQrcode />,
    },
  ];

  function setQuestionMeta(patch) {
    setQuestion(old => ({
      ...old,
      meta: {
        ...(old?.meta || {}),
        ...patch,
      },
    }));
  }

  const mapEmojisToData = (data, options) => {
    return data.map((item) => {
      const option = options.find((option) => option.slug === item.slug);

      if (option) {
        return {
          ...item,
          emoji: option.emoji,
        };
      }

      return item;
    });
  };

  const normalizeQuestionTypeSlug = slug => {
    const normalized = (slug || '').toLowerCase();
    if (normalized === 'wagon_plate') {
      return IMPLEMENT_PLATE_QUESTION_SLUG;
    }
    if (normalized === 'code') {
      return CODE_READER_QUESTION_SLUG;
    }
    return normalized;
  };

  async function fetchQuestionTypes() {
    try {
      const response = await cooperplaceApi.get('questions-types');
      const apiTypesRaw = Array.isArray(response.data) ? response.data : [];
      const apiTypes = apiTypesRaw.map(type => ({
        ...type,
        slug: normalizeQuestionTypeSlug(type?.slug),
      }));

      const extraTypes = [
        { id: -101, slug: VEHICLE_PLATE_QUESTION_SLUG, name: 'Placa tração' },
        { id: -102, slug: IMPLEMENT_PLATE_QUESTION_SLUG, name: 'Placa Implemento / Carroceria' },
        { id: -103, slug: CODE_READER_QUESTION_SLUG, name: 'Código' },
      ];

      const merged = [
        ...apiTypes,
        ...extraTypes.filter(extra => !apiTypes.some(t => t.slug === extra.slug)),
      ];

      const responseWithEmojis = mapEmojisToData(merged, questionTypeOptions);
      setQuestionTypes(responseWithEmojis);
    } catch (error) {}
  }

  useEffect(() => {
    fetchQuestionTypes();
  }, []);

  useEffect(() => {
    const mediaName = question?.media?.name;
    if (isVideoVisualizationType && mediaName) {
      setVideoFiles([{ name: mediaName, size: question?.media?.size || 0 }]);
      return;
    }
    if (!question?.mediaId && !question?.videoFile) {
      setVideoFiles([]);
    }
  }, [
    isVideoVisualizationType,
    question?.media?.name,
    question?.media?.size,
    question?.mediaId,
    question?.videoFile,
  ]);

  useEffect(() => {
    if (!isVideoVisualizationType) {
      setVideoPreviewUrl('');
      return;
    }

    const firstVideo = videoFiles?.[0];
    if (firstVideo instanceof File) {
      const localUrl = URL.createObjectURL(firstVideo);
      setVideoPreviewUrl(localUrl);
      return () => {
        URL.revokeObjectURL(localUrl);
      };
    }

    const media = question?.media || {};
    const remoteUrl =
      media?.full_path ||
      media?.fullPath ||
      media?.url ||
      media?.path ||
      media?.container ||
      '';
    setVideoPreviewUrl(remoteUrl || '');
  }, [
    isVideoVisualizationType,
    videoFiles,
    question?.media?.full_path,
    question?.media?.fullPath,
    question?.media?.url,
    question?.media?.path,
    question?.media?.container,
  ]);

  useEffect(() => {
    if (questionTypes?.length > 0) {
      setQuestionType(questionType ?? questionTypes[0]);
      setChildQuestionType(childQuestionType ?? questionTypes[0]);
    }
  }, [questionTypes]);

  async function fetchGroupsOptions() {
    try {
      const response = await api.get('groups', {
        params: {
          onlyActive: true,
        },
      });
      setGroupOptions(response.data);
      return;
    } catch (error) {
      setGroupOptions([]);
    }
  }

  useEffect(() => {
    fetchGroupsOptions();
  }, []);
  function handleAddAnswer(setter) {
    setter((old) => ({
      ...old,
      answers: [
        ...old?.answers,
        {
          id: old?.answers?.length + 1,
          order: old?.answers?.length + 1,
          description: '',
          isCorrect: false,
          triggerOccurrence: false,
          triggerSubQuestion: false,
        },
      ],
    }));
  }
  function handleRemoveParentAnswer(index) {
    const updatedAnswers = handleRemoveAnswer(index, [...question?.answers]);
    setQuestion((old) => ({ ...old, answers: [...updatedAnswers] }));
  }

  function handleRemoveChildAnswer(index) {
    const updatedAnswers = handleRemoveAnswer(index, [...childQuestion?.answers]);
    setChildQuestion((old) => ({ ...old, answers: [...updatedAnswers] }));
  }

  function handleRemoveAnswer(indexToRemove, answersList) {
    const updatedAnswers = answersList?.filter((_, idx) => idx !== indexToRemove);
    return updatedAnswers;
  }

  function handleOnDragEnd(event, answersList) {
    const { active, over } = event;
    if (active.id === over.id) {
      return;
    }
    const previousAnswersList = [...answersList];
    const oldIndex = previousAnswersList.findIndex((item) => item.id === active.id);
    const newIndex = previousAnswersList.findIndex((item) => item.id === over.id);
    const newAnswerListOrder = arrayMove(previousAnswersList, oldIndex, newIndex);
    const newAnswerListOrderWithOrderProp = newAnswerListOrder.map((item, index) => {
      return {
        ...item,
        order: index + 1,
      };
    });
    return newAnswerListOrderWithOrderProp;
  }
  function handleParentOnDragEnd(event) {
    const newOrder = handleOnDragEnd(event, question?.answers);
    setQuestion((old) => ({
      ...old,
      answers: newOrder?.length ? [...newOrder] : old?.answers,
    }));
  }

  function handleChildOnDragEnd(event) {
    const newOrder = handleOnDragEnd(event, childQuestion?.answers);
    setChildQuestion((old) => ({
      ...old,
      answers: newOrder?.length ? [...newOrder] : old?.answers,
    }));
  }

  const canAddNewAnswerOption = useMemo(() => {
    return (
      canRegisterAnswer &&
      (question?.questionDescription?.length || 0) > 0 &&
      (
        (question?.answers?.length || 0) === 0 ||
        (
          (question?.answers?.length || 0) >= 1 &&
          question?.answers?.every(item => (item?.description?.length || 0) >= 1)
        )
      )
    );
  }, [canRegisterAnswer, question?.answers, question?.questionDescription]);

  const addNewAnswerBlockedMessage = useMemo(() => {
    if (!canRegisterAnswer) {
      return 'Tipo de pergunta não permite inserir opções de resposta';
    }
    if ((question?.questionDescription?.length || 0) === 0) {
      return 'Informe uma pergunta para cadastrar opções de resposta';
    }
    if (
      (question?.answers?.length || 0) >= 1 &&
      question?.answers?.some(item => (item?.description?.length || 0) < 3)
    ) {
      return 'Termine de preencher alternativas anteriores antes de cadastrar uma nova';
    }
  }, [canRegisterAnswer, question?.answers, question?.questionDescription]);


  const canAddNewAnswerChildOption = useMemo(() => {
    return (
      QUESTION_TYPES_ALLOWED_TO_REGISTER_ANSWER.includes(childQuestionType?.slug) &&
      childQuestion?.questionDescription?.length > 0 &&
      (childQuestion?.answers?.length === 0 ||
        (childQuestion?.answers?.length >= 1 &&
          childQuestion?.answers?.every((item) => item?.description?.length >= 1)))
    );
  }, [childQuestion?.answers, childQuestion?.questionDescription, childQuestionType]);

  const addNewChildAnswerBlockedMessage = useMemo(() => {
    if (!QUESTION_TYPES_ALLOWED_TO_REGISTER_ANSWER.includes(childQuestionType?.slug)) {
      return 'Tipo de pergunta não permite inserir opções de resposta';
    }
    if (childQuestion?.questionDescription?.length === 0) {
      return 'Informe uma pergunta para cadastrar opções de resposta';
    }
    if (
      childQuestion?.answers?.length >= 1 &&
      childQuestion?.answers?.some((item) => item?.description?.length < 3)
    ) {
      return 'Termine de preencher alternativas anteriores antes de cadastrar uma nova';
    }
  }, [childQuestion?.answers, childQuestion?.questionDescription, childQuestionType]);

  const canGenerateOccurrence =
    QUESTION_TYPES_ALLOWED_TO_CREATE_OCCURRENCE.includes(questionType?.slug) &&
    !question?.hasCorrectAnswer;

  const canChildGenerateOccurrence = QUESTION_TYPES_ALLOWED_TO_CREATE_OCCURRENCE.includes(
    childQuestionType?.slug,
  );

  const canAddChildQuestion =
    QUESTION_TYPES_ALLOWED_TO_HAVE_CHILD_QUESTION.includes(
      questionType?.slug
    ) &&
    !question?.hasCorrectAnswer &&
    question?.questionDescription?.length >= 1 &&
    question.answers?.length > 0 &&
    question.answers.some((item) => item?.description?.length >= 1);

  const occurrencesTriggerOptions = question?.answers?.filter(
    (item) => item.description?.length >= 1,
  );

  const subQuestionTriggerOptions = question?.answers?.filter(
    (item) => item.description?.length >= 1,
  );

  const subQuestionOccurrencesTriggerOptions = childQuestion?.answers?.filter(
    (item) => item.description?.length >= 1,
  );

  const canRequestAttachment = QUESTION_TYPES_ALLOWED_TO_REQUEST_ATTACHMENT.includes(
    questionType?.slug,
  );

  const canConfigureCorrectAnswer =
    QUESTION_TYPES_ALLOWED_TO_HAVE_CORRECT_ANSWER.includes(questionType?.slug) &&
    !question?.hasSubQuestion &&
    !question?.createOccurrence;

  const allowsSingleCorrectAnswerOnly =
    QUESTION_TYPES_WITH_SINGLE_CORRECT_ANSWER.includes(questionType?.slug);

  const canTriggersBeMultiple =
    questionType?.slug !== UNIQUE_QUESTION_SLUG && questionType?.slug !== LOGICAL_QUESTION_SLUG;

  const canChildTriggersBeMultiple =
    childQuestionType?.slug !== UNIQUE_QUESTION_SLUG &&
    childQuestionType?.slug !== LOGICAL_QUESTION_SLUG;

  function handleHasCorrectAnswerChange() {
    const shouldEnable = !question?.hasCorrectAnswer;
    setQuestion(old => ({
      ...old,
      hasCorrectAnswer: shouldEnable,
      hasSubQuestion: shouldEnable ? false : old?.hasSubQuestion,
      createOccurrence: shouldEnable ? false : old?.createOccurrence,
      occurrenceSector: shouldEnable ? null : old?.occurrenceSector,
      answers: shouldEnable
        ? old?.answers?.map(item => ({
          ...item,
          triggerSubQuestion: false,
          triggerOccurrence: false,
        }))
        : old?.answers?.map(item => ({ ...item, isCorrect: false })),
    }));
    if (shouldEnable) {
      setChildQuestion({ hasSubQuestion: false, answers: [], active: true });
      setChildQuestionType(null);
    }
  }

  function handleAnswerCorrectChange(index) {
    setQuestion(old => ({
      ...old,
      answers: old?.answers?.map((item, idx) => {
        if (allowsSingleCorrectAnswerOnly) {
          return { ...item, isCorrect: idx === index ? !item?.isCorrect : false };
        }

        if (idx === index) {
          return { ...item, isCorrect: !item?.isCorrect };
        }

        return item;
      }),
    }));
  }

  function handleQuestionTypeChange(value) {
    const slug = value?.slug?.toLowerCase();
    const canTypeHaveCorrectAnswer =
      QUESTION_TYPES_ALLOWED_TO_HAVE_CORRECT_ANSWER.includes(slug);

    if (FIXED_QUESTION_TYPES.includes(slug)) {
      setQuestion(old => ({
        ...old,
        questionDescription: FIXED_QUESTION_TEXT_BY_SLUG[slug] || '',
        answers: [],
        hasCorrectAnswer: false,
        createOccurrence: false,
        hasSubQuestion: false,
        needAttachment: false,
        occurrenceSector: null,

        meta: {
          ...(old?.meta || {}),
          vehiclePlate: null,
          implementPlate: null,
          codeValue: null,
          codeType: null,
        },
      }));

      setErrors({});
      setQuestionType(value);
      return;
    }

    setQuestion(old => ({
      ...old,
      meta: {
        ...(old?.meta || {}),
        vehiclePlate: null,
        implementPlate: null,
        codeValue: null,
        codeType: null,
      },
      ...(slug === VIDEO_VISUALIZATION_QUESTION_SLUG
        ? {}
        : {
          mediaId: null,
          media: null,
        }),
    }));

    if (question?.createOccurrence && FIXED_QUESTION_TYPES.includes(slug)) {
      const updatedAnswers = (question?.answers || []).map(item => ({
        ...item,
        triggerOccurrence: false,
        triggerSubQuestion: false,
      }));

      setQuestion(old => ({
        ...old,
        createOccurrence: false,
        answers: updatedAnswers,
        hasSubQuestion: false,
        occurrenceSector: null,
      }));
    }

    if (slug === LOGICAL_QUESTION_SLUG) {
      setQuestion(old => ({
        ...old,
        questionDescription: old?.questionDescription || '',
        hasCorrectAnswer: canTypeHaveCorrectAnswer
          ? old?.hasCorrectAnswer
          : false,
        answers: [...LOGICAL_QUESTION_ANSWERS].map(item => ({
          ...item,
          isCorrect: false,
        })),
      }));
    } else if (slug === VIDEO_VISUALIZATION_QUESTION_SLUG) {
      setQuestion(old => ({
        ...old,
        answers: [],
        hasCorrectAnswer: false,
        createOccurrence: false,
        hasSubQuestion: false,
        needAttachment: false,
        occurrenceSector: null,
      }));
    } else if (slug === IMAGE_QUESTION_SLUG) {
      setQuestion(old => ({
        ...old,
        answers: [],
        hasCorrectAnswer: false,
        createOccurrence: false,
        hasSubQuestion: false,
        needAttachment: false,
        occurrenceSector: null,
      }));
    } else {
      setQuestion(old => ({
        ...old,
        hasCorrectAnswer: canTypeHaveCorrectAnswer ? old?.hasCorrectAnswer : false,
        answers: [],
      }));
    }

    setErrors({});
    setQuestionType(value);
  }

  function handleVideoUpload(file) {
    if (!file) return;

    const extension = (file?.name || '').split('.').pop()?.toLowerCase();

    if (!ALLOWED_VIDEO_EXTENSIONS.includes(extension)) {
      setVideoUploadError(
        'Formato inválido. Envie .mp4, .m4v, .mpeg, .mpg, .mkv ou .mov'
      );
      return;
    }

    if (file?.size > MAX_VIDEO_SIZE_BYTES) {
      setVideoUploadError(`O vídeo excede o limite de ${MAX_VIDEO_SIZE_LABEL}.`);
      return;
    }

    if (file?.type && !ALLOWED_VIDEO_MIME_TYPES.includes(file.type)) {
      setVideoUploadError(
        'Codec/tipo não suportado. Tente exportar em MP4 (H.264/AAC).'
      );
      return;
    }

    setVideoUploadError('');
    setQuestion(old => ({
      ...old,
      videoFile: file,
      mediaId: null,
      media: null,
    }));
    setVideoFiles([file]);
  }

  function handleRemoveUploadedVideo() {
    setVideoUploadError('');
    setVideoFiles([]);
    setVideoPreviewUrl('');
    setQuestion(old => ({
      ...old,
      videoFile: null,
      mediaId: null,
      media: null,
    }));
  }

  function handleChildQuestionTypeChange(value) {
    if (childQuestion?.createOccurrence) {
      const updatedAnswers = childQuestion.answers.map((item) => {
        return { ...item, triggerOccurrence: false };
      });
      setChildQuestion((old) => ({
        ...old,
        createOccurrence: false,
        answers: updatedAnswers,
      }));
    }
    if (value?.slug?.toLocaleLowerCase() === LOGICAL_QUESTION_SLUG) {
      setChildQuestion((old) => ({
        ...old,
        answers: [...LOGICAL_QUESTION_ANSWERS],
      }));
    }
    if (
      value?.slug?.toLocaleLowerCase() === IMAGE_QUESTION_SLUG ||
      value?.slug?.toLocaleLowerCase() === DESCRIPTIVE_QUESTION_SLUG
    ) {
      setChildQuestion((old) => ({
        ...old,
        answers: [],
      }));
    }
    setChildQuestionType(value);
  }

  const isParentOccurrenceBlocked = childQuestion?.createOccurrence;
  const isChildOccurrenceBlocked = question?.createOccurrence;

  function handleOnSearchGroup(search) {
    if (!search) {
      return groupOptions;
    }
    const parsed = search?.toLocaleLowerCase();
    return groupOptions?.filter((option) => option?.name?.toLocaleLowerCase().search(parsed) >= 0);
  }
  return (
    <>
      <Row className="my-2 pb-4">
        <QuestionTypeFieldContainer lg={4} md={4} sm={12}>
          <Select.Async
            label="Tipo de pergunta *"
            options={questionTypes}
            onChange={(value) => {
              handleQuestionTypeChange(value);
            }}
            getOptionLabel={(option) => (
              <span>
                {option?.emoji} {option?.name}
              </span>
            )}
            getOptionValue={(option) => option.id}
            value={questionType}
            disableClear
            onSearch={() => {}}
          />
        </QuestionTypeFieldContainer>
        <QuestionGeneralInfoContainer>
          {canRequestAttachment && (
            <div>
              <Text as="span" color="dark" type="medium" weight={500}>
                Exige anexo de imagem?
              </Text>
              <CheckBox
                label="Sim"
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                }}
                value={question?.needAttachment}
                checked={question?.needAttachment}
                onChange={() =>
                  setQuestion((old) => ({
                    ...old,
                    needAttachment: !old?.needAttachment,
                  }))
                }
              />
            </div>
          )}
          {canConfigureCorrectAnswer && (
            <div>
              <Text as="span" color="dark" type="medium" weight={500}>
                Possui resposta correta?
              </Text>
              <CheckBox
                label="Sim"
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                }}
                value={question?.hasCorrectAnswer}
                checked={question?.hasCorrectAnswer}
                onChange={handleHasCorrectAnswerChange}
              />
            </div>
          )}
          {!isRegister && (
            <div>
              <Text
                title="Pergunta está ativa ou inativa"
                as="span"
                color="dark"
                type="medium"
                weight={500}
              >
                Status:
              </Text>
              <CheckBox
                label="Ativa"
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                }}
                checked={question?.active}
                value={question?.active}
                onChange={() =>
                  setQuestion(old => ({
                    ...old,
                    active: !old?.active,
                  }))
                }
              />
            </div>
          )}
          {canGenerateOccurrence && (
            <div>
              <Text as="span" color="dark" type="medium" weight={500}>
                  Gera ocorrência?
                </Text>
              <CheckBox
                label="Sim"
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                }}
                checked={question?.createOccurrence}
                value={question?.createOccurrence}
                onChange={() => {
                  if (question?.createOccurrence) {
                    const updatedAnswers = question?.answers?.map((item) => {
                      return { ...item, triggerOccurrence: false };
                    });
                    setQuestion((old) => ({
                      ...old,
                      createOccurrence: !old?.createOccurrence,
                      hasCorrectAnswer: false,
                      answers: updatedAnswers,
                    }));
                  } else {
                    const updatedAnswers = question?.answers?.map((item) => {
                      return { ...item, isCorrect: false };
                    });
                    setQuestion((old) => ({
                      ...old,
                      createOccurrence: !old?.createOccurrence,
                      hasCorrectAnswer: false,
                      answers: updatedAnswers,
                    }));
                  }
                }}
                disabled={isParentOccurrenceBlocked}
              />
            </div>
          )}
        </QuestionGeneralInfoContainer>
      </Row>

      <Row className="my-2">
        <Col md={10} sm={12}>
          {isVideoVisualizationType && (
            <>
              <TextArea
                label="Informações do vídeo (titulo, treinamento, etc) *"
                value={question?.questionDescription || ''}
                maxLength={800}
                onChange={e => {
                  const content = e.target.value;
                  setQuestion(old => ({
                    ...old,
                    questionDescription: content,
                  }));
                }}
                error={
                  errors?.['question.questionDescription'] ||
                  errors?.['question.answers']
                }
              />
              {(question?.questionDescription?.length || 0) >= 800 && (
                <div style={{ color: '#FF4D4F', fontSize: 12, marginTop: 4 }}>
                  Limite máximo de 800 caracteres atingido.
                </div>
              )}
            </>
          )}
          {isFixedQuestionType && (
            <TextArea
              label="Informe a pergunta *"
              value={question?.questionDescription || ''}
              disabled
              resizable={false}
              minHeight="85px"
            />
          )}

          {!isFixedQuestionType && !isVideoVisualizationType && (
            <>
              <TextArea
                label="Informe a pergunta *"
                value={question?.questionDescription || ''}
                maxLength={800}
                onChange={e => {
                  const content = e.target.value;
                  setQuestion(old => ({
                    ...old,
                    questionDescription: content,
                  }));
                }}
                error={
                  errors?.['question.questionDescription'] ||
                  errors?.['question.answers']
                }
              />
              {(question?.questionDescription?.length || 0) >= 800 && (
                <div style={{ color: '#FF4D4F', fontSize: 12, marginTop: 4 }}>
                  Limite máximo de 800 caracteres atingido.
                </div>
              )}
            </>
          )}
        </Col>
        <AddAnswerButtonContainer md={2} sm={12}>
          {canShowInsertAnswerButton ? (
            <AddAnswerButton
              onClick={() => handleAddAnswer(setQuestion)}
              disabled={!canAddNewAnswerOption}
              title={!canAddNewAnswerOption ? addNewAnswerBlockedMessage : undefined}
            >
              <FaPlus />
              <Text type="body" color="dark" weight="500">
                Inserir resposta
              </Text>
            </AddAnswerButton>
          ) : null}
        </AddAnswerButtonContainer>
      </Row>
      {isVideoVisualizationType && (
        <Row className="my-2">
          <Col md={12} sm={12}>
            <UploadChecklistMedia
              name="question-video"
              id="question-video"
              accept=".mp4,.m4v,.mpeg,.mpg,.mkv,.mov"
              label="Upload de vídeo"
              helperText={`Formatos aceitos: ${VIDEO_ACCEPTED_FORMATS_LABEL}. Tamanho máximo: ${MAX_VIDEO_SIZE_LABEL}.`}
              multiple={false}
              disabled={false}
              files={videoFiles}
              hideFilesList
              onFilesChange={files => {
                const [file] = files || [];
                if (file) handleVideoUpload(file);
              }}
              onRemove={() => {
                handleRemoveUploadedVideo();
              }}
              error={errors?.['question.mediaId'] || videoUploadError}
            />
          </Col>
        </Row>
      )}
      {isVideoVisualizationType && !!videoPreviewUrl && (
        <Row className="my-2">
          <Col md={12} sm={12}>
            <div
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                padding: 12,
                background: '#F9FAFB',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <Text type="label" weight="500" color="dark">
                  Pré-visualização do vídeo
                </Text>
                <button
                  type="button"
                  onClick={handleRemoveUploadedVideo}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#DC2626',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: 2,
                  }}
                  title="Remover vídeo"
                  aria-label="Remover vídeo"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </button>
              </div>

              <video
                controls
                style={{
                  width: '100%',
                  maxHeight: 360,
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  background: '#000',
                }}
              >
                <source src={videoPreviewUrl} />
                <track kind="captions" />
              </video>
            </div>
          </Col>
        </Row>
      )}
      {!isVideoVisualizationType && (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={event => handleParentOnDragEnd(event)}
        >
          <SortableContext
            items={question?.answers}
            strategy={verticalListSortingStrategy}
          >
            {question?.answers?.map((item, index) => {
              return (
                <SortableAnswer
                  key={item?.id}
                  answer={item}
                  index={index}
                  question={question}
                  setQuestion={setQuestion}
                  handleRemoveAnswer={handleRemoveParentAnswer}
                  questionType={questionType}
                  showCorrectOption={question?.hasCorrectAnswer}
                  onToggleCorrect={handleAnswerCorrectChange}
                  errors={errors}
                >
                  {JSON.stringify(item)}
                </SortableAnswer>
              );
            })}
          </SortableContext>
        </DndContext>
      )}
      {question?.createOccurrence && (
        <Row className="my-2">
          <Col md={6} sm={12}>
            <Select
              label="Gatilho ocorrência *"
              tooltip="Resposta que dispara ocorrência"
              options={occurrencesTriggerOptions}
              getOptionLabel={(option) => option.description}
              getOptionValue={(option) => option.id}
              multiple={canTriggersBeMultiple}
              onChange={(value) => {
                if (canTriggersBeMultiple) {
                  if (value?.length > 0) {
                    const updated = question?.answers?.map((item) => ({
                      ...item,
                      triggerOccurrence: value?.some((option) => option.id === item.id),
                    }));
                    setQuestion((old) => ({ ...old, answers: updated }));
                  } else {
                    setQuestion((old) => ({
                      ...old,
                      answers: old?.answers?.map((item) => {
                        return { ...item, triggerOccurrence: false };
                      }),
                    }));
                  }
                } else if (value) {
                  const updated = question?.answers?.map((item) => ({
                    ...item,
                    triggerOccurrence: value?.id === item?.id,
                  }));
                  setQuestion((old) => ({ ...old, answers: updated }));
                } else {
                  setQuestion((old) => ({
                    ...old,
                    answers: old?.answers?.map((item) => {
                      return { ...item, triggerOccurrence: false };
                    }),
                  }));
                }
              }}
              value={question?.answers?.filter((item) => item?.triggerOccurrence)}
              noOptionsMessageText={
                question?.answers?.length === 0 && 'Insira respostas para gerar opções'
              }
              error={errors?.['question.occurrenceTrigger']}
            />
          </Col>
          <Col md={6} sm={12}>
            <Select.Async
              label="Setor responsável *"
              onSearch={(search) => handleOnSearchGroup(search)}
              tooltip="Para qual setor a ocorrência será encaminhada"
              value={question?.occurrenceSector}
              onChange={(value) => setQuestion((old) => ({ ...old, occurrenceSector: value }))}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              options={groupOptions}
              error={errors?.['question.occurrenceSector']}
            />
          </Col>
        </Row>
      )}
      {canAddChildQuestion && (
        <>
          <Row className="my-2">
            <Col md={6} sm={12}>
              <div>
                <Text as="span" color="dark" type="medium" weight={500}>
                  Incluir subpergunta?
                </Text>
                <CheckBox
                  label="Sim"
                  labelStyle={{
                    textTransform: 'capitalize',
                    color: '#464E5F',
                  }}
                  checked={question?.hasSubQuestion}
                  value={question?.hasSubQuestion}
                  onChange={() =>
                    setQuestion((old) => ({
                      ...old,
                      hasSubQuestion: !old?.hasSubQuestion,
                      hasCorrectAnswer: !old?.hasSubQuestion
                        ? false
                        : old?.hasCorrectAnswer,
                      answers: !old?.hasSubQuestion
                        ? old?.answers?.map(item => ({ ...item, isCorrect: false }))
                        : old?.answers,
                    }))
                  }
                />
              </div>
            </Col>
            {question?.hasSubQuestion && (
              <Col md={6} sm={12}>
                <Select
                  label="Gatilho para subpergunta"
                  tooltip="Respostas vinculadas à subpergunta"
                  options={subQuestionTriggerOptions}
                  getOptionLabel={(option) => option.description}
                  getOptionValue={(option) => option.id}
                  multiple={canTriggersBeMultiple}
                  onChange={(value) => {
                    if (canTriggersBeMultiple) {
                      if (value?.length > 0) {
                        const updated = question?.answers?.map((item) => ({
                          ...item,
                          triggerSubQuestion: value?.some((option) => option.id === item.id),
                        }));
                        setQuestion((old) => ({
                          ...old,
                          answers: updated,
                        }));
                      } else {
                        setQuestion((old) => ({
                          ...old,
                          answers: old?.answers?.map((item) => {
                            return { ...item, triggerSubQuestion: false };
                          }),
                        }));
                      }
                    } else if (value) {
                      const updated = question?.answers?.map((item) => ({
                        ...item,
                        triggerSubQuestion: value?.id === item?.id,
                      }));
                      setQuestion((old) => ({
                        ...old,
                        answers: updated,
                      }));
                    } else {
                      setQuestion((old) => ({
                        ...old,
                        answers: old?.answers?.map((item) => {
                          return { ...item, triggerSubQuestion: false };
                        }),
                      }));
                    }
                  }}
                  value={question?.answers?.filter((item) => item?.triggerSubQuestion)}
                  noOptionsMessageText={
                    question?.answers?.length === 0 && 'Insira respostas para gerar opções'
                  }
                  error={errors?.['question.subQuestionTrigger']}
                />
              </Col>
            )}
          </Row>
          {question?.hasSubQuestion &&
            question?.answers?.filter((item) => item?.triggerSubQuestion)?.length > 0 && (
              <>
                <Row>
                  <Col md={12}>
                    <Text type="regular" weight={500} color="dark">
                      Dados subpergunta
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col md={6} sm={12}>
                    <Select.Async
                      label="Tipo de pergunta *"
                      options={questionTypes}
                      onChange={(value) => {
                        handleChildQuestionTypeChange(value);
                      }}
                      getOptionLabel={(option) => (
                        <span>
                          {option?.emoji} {option?.name}
                        </span>
                      )}
                      getOptionValue={(option) => option.id}
                      value={childQuestionType || questionTypes[0]}
                      disableClear
                      error={errors.childQuestionType}
                    />
                  </Col>
                  <QuestionGeneralInfoContainer>
                    {canChildGenerateOccurrence && (
                      <div>
                        <Text as="span" color="dark" type="medium" weight={500}>
                          Gera ocorrência
                        </Text>
                        <CheckBox
                          label="Sim"
                          labelStyle={{
                            textTransform: 'capitalize',
                            color: '#464E5F',
                          }}
                          checked={childQuestion?.createOccurrence}
                          value={childQuestion?.createOccurrence}
                          onChange={() =>
                            setChildQuestion((old) => ({
                              ...old,
                              createOccurrence: !old?.createOccurrence,
                            }))
                          }
                          disabled={isChildOccurrenceBlocked}
                        />
                      </div>
                    )}
                  </QuestionGeneralInfoContainer>
                </Row>
                <Row className="my-2">
                  <Col md={10} sm={12}>
                    <>
                      <TextArea
                        label="Informe a pergunta"
                        value={childQuestion?.questionDescription}
                        maxLength={800}
                        onChange={e => {
                          const content = e.target.value;
                          setChildQuestion(old => ({
                            ...old,
                            questionDescription: content,
                          }));
                        }}
                        error={errors?.['childQuestion.questionDescription']}
                      />
                      {(childQuestion?.questionDescription?.length || 0) >= 800 && (
                        <div
                          style={{ color: '#FF4D4F', fontSize: 12, marginTop: 4 }}
                        >
                          Limite máximo de 800 caracteres atingido.
                        </div>
                      )}
                    </>
                  </Col>
                  <AddAnswerButtonContainer md={2} sm={12}>
                    <AddAnswerButton
                      onClick={() => handleAddAnswer(setChildQuestion)}
                      disabled={!canAddNewAnswerChildOption}
                      title={!canAddNewAnswerChildOption && addNewChildAnswerBlockedMessage}
                    >
                      <FaPlus />
                      <Text type="body" color="dark" weight="500">
                        Inserir resposta
                      </Text>
                    </AddAnswerButton>
                  </AddAnswerButtonContainer>
                </Row>
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleChildOnDragEnd(event)}
                >
                  <SortableContext
                    items={childQuestion?.answers}
                    strategy={verticalListSortingStrategy}
                  >
                    {childQuestion?.answers?.map((item, index) => {
                      return (
                        <SortableAnswer
                          key={item?.id}
                          answer={item}
                          index={index}
                          question={childQuestion}
                          setQuestion={setChildQuestion}
                          handleRemoveAnswer={handleRemoveChildAnswer}
                          questionType={childQuestionType}
                          showCorrectOption={false}
                          onToggleCorrect={() => {}}
                        >
                          {JSON.stringify(item)}
                        </SortableAnswer>
                      );
                    })}
                  </SortableContext>
                </DndContext>
                {childQuestion?.createOccurrence && (
                  <Row className="my-2">
                    <Col md={6} sm={12}>
                      <Select
                        label="Gatilho ocorrência"
                        tooltip="Resposta que dispara ocorrência"
                        options={subQuestionOccurrencesTriggerOptions}
                        getOptionLabel={(option) => option.description}
                        getOptionValue={(option) => option.id}
                        multiple={canChildTriggersBeMultiple}
                        onChange={(value) => {
                          if (canChildTriggersBeMultiple) {
                            if (value?.length > 0) {
                              const updated = childQuestion?.answers?.map((item) => ({
                                ...item,
                                triggerOccurrence: value?.some((option) => option.id === item.id),
                              }));
                              setChildQuestion((old) => ({
                                ...old,
                                answers: updated,
                              }));
                            } else {
                              setChildQuestion((old) => ({
                                ...old,
                                answers: old?.answers?.map((item) => {
                                  return {
                                    ...item,
                                    triggerOccurrence: false,
                                  };
                                }),
                              }));
                            }
                          } else if (value) {
                            const updated = childQuestion?.answers?.map((item) => ({
                              ...item,
                              triggerOccurrence: value?.id === item?.id,
                            }));
                            setChildQuestion((old) => ({
                              ...old,
                              answers: updated,
                            }));
                          } else {
                            setChildQuestion((old) => ({
                              ...old,
                              answers: old?.answers?.map((item) => {
                                return {
                                  ...item,
                                  triggerOccurrence: false,
                                };
                              }),
                            }));
                          }
                        }}
                        value={childQuestion?.answers?.filter((item) => item?.triggerOccurrence)}
                        noOptionsMessageText={
                          childQuestion?.answers?.length === 0 &&
                          'Insira respostas para gerar opções'
                        }
                        error={errors?.['childQuestion.occurrenceTrigger']}
                      />
                    </Col>
                    <Col md={6} sm={12}>
                      <Select.Async
                        label="Setor responsável"
                        onSearch={(search) => handleOnSearchGroup(search)}
                        tooltip="Para qual setor a ocorrência será encaminhada"
                        value={childQuestion?.occurrenceSector}
                        onChange={(value) =>
                          setChildQuestion((old) => ({
                            ...old,
                            occurrenceSector: value,
                          }))
                        }
                        getOptionLabel={(option) => option?.name}
                        getOptionValue={(option) => option?.id}
                        error={errors?.['childQuestion.occurrenceSector']}
                        options={groupOptions}
                      />
                    </Col>
                  </Row>
                )}
              </>
            )}
        </>
      )}
    </>
  );
}
